import React, { useMemo } from 'react';

import { isEven, isLandscape } from '@libs/NwUtils';

import { usePlaylistEditor, usePlaylistEditorDispatch } from '@components/PlaylistEditor/PlaylistEditorContext';
import { isPlaceholder } from '@components/PlaylistEditor/PlaylistEditorUtils';

import MobilePageCard from './MobilePageCard';
import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

const isWideItem = (playlistItem) => !isPlaceholder(playlistItem) && isLandscape(playlistItem.media);

/**
 * Raggruppa gli item in facciate affiancate seguendo la numerazione calcolata da
 * `formatPages`: una pagina pari chiude lo spread, un media orizzontale ne occupa
 * uno da solo perche' vale due pagine.
 */
const groupIntoSpreads = (playlistItems) => {
    const rows = [];
    let currentRow = [];

    playlistItems.forEach((playlistItem, index) => {
        const entry = { playlistItem, index };
        if (isWideItem(playlistItem)) {
            if (currentRow.length) {
                rows.push(currentRow);
                currentRow = [];
            }
            rows.push([entry]);
        } else {
            currentRow.push(entry);
            if (isEven(playlistItem.page)) {
                rows.push(currentRow);
                currentRow = [];
            }
        }
    });

    if (currentRow.length) {
        rows.push(currentRow);
    }
    return rows;
};

const spreadFolio = (row) => {
    const first = row[0].playlistItem;
    const last = row[row.length - 1].playlistItem;
    return {
        from: isWideItem(first) ? first.page - 1 : first.page,
        to: last.page,
    };
};

const DropRail = ({ orientation, targetIndex, onPlace }) => (
    <button
        type="button"
        className={`book-drop-rail ${orientation}`}
        aria-label={`Place here, position ${targetIndex + 1}`}
        onClick={() => onPlace(targetIndex)}
    >
        <i />
    </button>
);

const MobileBookCanvas = ({ movingID, selectedID, onSelect, onMove, onPlace, onPreview, onRemove }) => {
    const { playlistItems, singlePage } = usePlaylistEditor();
    const dispatch = usePlaylistEditorDispatch();

    const isPlacing = Boolean(movingID);

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
        // il ritardo lascia il tap al menu della pagina e lo scroll al dito
        useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } })
    );

    const sortableIds = useMemo(() => playlistItems.map((item) => `sortable-${item.ID}`), [playlistItems]);

    const spreadRows = useMemo(
        () => (singlePage ? [] : groupIntoSpreads(playlistItems)),
        [playlistItems, singlePage]
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === active.id);
        const newIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
            dispatch({ type: 'MovePlaylistItem', value: { oldIndex, newIndex } });
        }
    };

    const renderPage = (entry) => (
        <MobilePageCard
            playlistItem={entry.playlistItem}
            showPageNumber={singlePage}
            isSelected={selectedID === entry.playlistItem.ID}
            isInHand={movingID === entry.playlistItem.ID}
            isPlacing={isPlacing}
            onSelect={onSelect}
            onMove={onMove}
            onPreview={onPreview}
            onRemove={onRemove}
        />
    );

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                {singlePage ? (
                    <div className="book-sequence">
                        {playlistItems.map((playlistItem, index) => (
                            <React.Fragment key={playlistItem.ID}>
                                {isPlacing && (
                                    <DropRail orientation="horizontal" targetIndex={index} onPlace={onPlace} />
                                )}
                                {renderPage({ playlistItem, index })}
                            </React.Fragment>
                        ))}
                        {isPlacing && (
                            <DropRail
                                orientation="horizontal"
                                targetIndex={playlistItems.length}
                                onPlace={onPlace}
                            />
                        )}
                    </div>
                ) : (
                    <div className="book-spreads">
                        {spreadRows.map((row) => {
                            const folio = spreadFolio(row);
                            return (
                                <React.Fragment key={row[0].playlistItem.ID}>
                                    {isPlacing && (
                                        <DropRail
                                            orientation="horizontal"
                                            targetIndex={row[0].index}
                                            onPlace={onPlace}
                                        />
                                    )}
                                    <div>
                                        <div className="book-spread">
                                            {row.map((entry, position) => (
                                                <React.Fragment key={entry.playlistItem.ID}>
                                                    {isPlacing && position === 1 && (
                                                        <DropRail
                                                            orientation="vertical"
                                                            targetIndex={entry.index}
                                                            onPlace={onPlace}
                                                        />
                                                    )}
                                                    <div className="spread-slot">{renderPage(entry)}</div>
                                                </React.Fragment>
                                            ))}
                                            {row.length === 1 && !isWideItem(row[0].playlistItem) && (
                                                <div className="spread-slot" />
                                            )}
                                        </div>
                                        <div className="spread-folio">
                                            <span>{folio.from}</span>
                                            <span>{folio.to}</span>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        {isPlacing && (
                            <DropRail
                                orientation="horizontal"
                                targetIndex={playlistItems.length}
                                onPlace={onPlace}
                            />
                        )}
                    </div>
                )}
            </SortableContext>
        </DndContext>
    );
};

export default MobileBookCanvas;
